import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { ArrowRight, TrendingUp, Star, DollarSign, Zap, Share2, Check } from "lucide-react";
import { useSpringCount } from "../hooks/motion";
import { CALCOM_DEMO_URL } from "../config/demo";

/* ── Slider component ────────────────────────────────────── */
function Slider({ label, value, min, max, step, format, onChange }) {
  const pct = ((value - min) / (max - min)) * 100;
  const trackStyle = {
    background: `linear-gradient(to right, #71DED6 0%, #71DED6 ${pct}%, #1E1E2E ${pct}%, #1E1E2E 100%)`,
  };

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-[#8B8BA7] tracking-wide">{label}</label>
        <span className="text-xl font-bold text-[#F4F4F8] tabular-nums" data-testid={`slider-value-${label.toLowerCase().replace(/\s+/g, '-')}`}>
          {format(value)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="roi-slider"
        style={trackStyle}
        data-testid={`slider-${label.toLowerCase().replace(/\s+/g, '-')}`}
      />
      <div className="flex justify-between text-[10px] text-[#3A3A5A] font-medium tracking-wide">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

/* ── Stat card ───────────────────────────────────────────── */
function StatCard({ icon: Icon, value, prefix = "", suffix = "", label, sublabel, large = false, glow = false }) {
  const animVal = useSpringCount(value);
  const formatted = animVal.toLocaleString();

  return (
    <div
      className={`relative rounded-2xl p-6 border transition-all duration-300 ${
        glow
          ? "bg-[#0A1F12] border-[#71DED6]/30 annual-revenue-glow"
          : "bg-[#111118] border-[#1E1E2E]"
      } ${large ? "col-span-2" : ""}`}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {glow && (
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#71DED6]/5 to-transparent pointer-events-none" />
      )}
      <div className="relative z-10">
        <div className={`flex items-center gap-2 mb-3 ${glow ? "text-[#71DED6]" : "text-[#8B8BA7]"}`}>
          <Icon className={glow ? "w-5 h-5" : "w-4 h-4"} />
          <span className={`font-semibold uppercase tracking-widest ${glow ? "text-xs text-[#71DED6]" : "text-[10px] text-[#5A5A7A]"}`}>
            {label}
          </span>
        </div>
        <div className={`font-black tracking-tight text-[#71DED6] tabular-nums leading-none ${large ? "text-5xl md:text-6xl" : "text-3xl md:text-4xl"}`}>
          {prefix}{formatted}{suffix}
        </div>
        <p className={`mt-2 ${large ? "text-sm text-[#6A8A72]" : "text-xs text-[#4A4A6A]"} leading-snug`}>
          {sublabel}
        </p>
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────── */
export default function ROICalculatorPage() {
  usePageTitle("ROI Calculator | CleanBid.ai");

  const [searchParams] = useSearchParams();

  // Pre-populate from URL params if present
  const [jobValue, setJobValue] = useState(() => {
    const v = Number(searchParams.get("jobValue"));
    return v >= 50 && v <= 2000 ? v : 350;
  });
  const [jobsPerMonth, setJobsPerMonth] = useState(() => {
    const v = Number(searchParams.get("jobsPerMonth"));
    return v >= 5 && v <= 200 ? v : 30;
  });
  const [closeRate, setCloseRate] = useState(() => {
    const v = Number(searchParams.get("closeRate"));
    return v >= 10 && v <= 90 ? v : 40;
  });
  const [monthlyReviews, setMonthlyReviews] = useState(() => {
    const raw = searchParams.get("reviews");
    const v = raw !== null ? Number(raw) : NaN;
    return Number.isFinite(v) && v >= 0 && v <= 50 ? v : 8;
  });
  const [copied, setCopied] = useState(false);

  // Calculations
  const closeRateDecimal = closeRate / 100;
  const additionalJobs = Math.round(jobsPerMonth * 0.47 * (1 - closeRateDecimal) * 10) / 10;
  const additionalJobsRounded = Math.round(additionalJobs);
  const additionalMonthlyRevenue = Math.round(additionalJobsRounded * jobValue);
  const additionalReviews = Math.max(1, Math.round(jobsPerMonth * 3.2));
  const annualRevenue = additionalMonthlyRevenue * 12;
  const breakEvenJobs = Math.ceil(179 / jobValue);

  const handleShare = () => {
    const url = new URL(window.location.href);
    url.pathname = "/roi-calculator";
    url.search = `?jobValue=${jobValue}&jobsPerMonth=${jobsPerMonth}&closeRate=${closeRate}&reviews=${monthlyReviews}`;
    navigator.clipboard.writeText(url.toString()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="bg-[#0A0A0F] pt-20 min-h-screen" data-testid="roi-calculator-page">

      {/* ── Page Header ── */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 bg-[#71DED6]/10 border border-[#71DED6]/20 rounded-full px-4 py-1.5 mb-6">
            <TrendingUp className="w-3.5 h-3.5 text-[#71DED6]" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[#71DED6]">
              ROI Calculator
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#F4F4F8] leading-[1.05] mb-6">
            How much revenue are you<br />
            <span className="text-[#71DED6]">leaving on the table?</span>
          </h1>
          <p className="text-lg text-[#8B8BA7] leading-relaxed max-w-xl mx-auto">
            Move the sliders to see what automated follow-ups and review requests are worth to your business.
          </p>
        </div>
      </section>

      {/* ── Calculator Card ── */}
      <section className="pb-16 md:pb-24">
        <div className="container mx-auto px-4 md:px-6 max-w-[680px]">
          <div className="bg-[#111118] border border-[#1E1E2E] rounded-3xl overflow-hidden shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)]">

            {/* Inputs */}
            <div className="p-8 md:p-10 border-b border-[#1E1E2E] space-y-8">
              <div className="flex items-center gap-2.5 mb-2">
                <Zap className="w-4 h-4 text-[#71DED6]" />
                <h2 className="text-sm font-semibold uppercase tracking-widest text-[#8B8BA7]">
                  Your Business Numbers
                </h2>
              </div>

              <Slider
                label="Average job value"
                value={jobValue}
                min={50}
                max={2000}
                step={25}
                format={(v) => `$${v.toLocaleString()}`}
                onChange={setJobValue}
              />
              <Slider
                label="Jobs per month"
                value={jobsPerMonth}
                min={5}
                max={200}
                step={5}
                format={(v) => `${v}`}
                onChange={setJobsPerMonth}
              />
              <Slider
                label="Current quote close rate"
                value={closeRate}
                min={10}
                max={90}
                step={5}
                format={(v) => `${v}%`}
                onChange={setCloseRate}
              />
              <Slider
                label="Monthly Google reviews"
                value={monthlyReviews}
                min={0}
                max={50}
                step={1}
                format={(v) => `${v}`}
                onChange={setMonthlyReviews}
              />
            </div>

            {/* Outputs */}
            <div className="p-8 md:p-10 space-y-6">
              <div className="flex items-center gap-2.5 mb-2">
                <TrendingUp className="w-4 h-4 text-[#71DED6]" />
                <h2 className="text-sm font-semibold uppercase tracking-widest text-[#8B8BA7]">
                  Your Results with CleanBid
                </h2>
              </div>

              {/* Annual revenue — hero stat */}
              <StatCard
                icon={DollarSign}
                value={annualRevenue}
                prefix="$"
                label="Annual revenue opportunity"
                sublabel="Every year you wait costs you this"
                large
                glow
              />

              {/* Share button */}
              <button
                onClick={handleShare}
                data-testid="share-results-button"
                className={`w-full flex items-center justify-center gap-2 border rounded-xl py-2.5 text-sm font-semibold transition-all duration-200 ${
                  copied
                    ? "border-[#71DED6]/40 text-[#71DED6] bg-[#71DED6]/8"
                    : "border-[#71DED6]/30 text-[#71DED6] bg-transparent hover:border-[#71DED6]/60 hover:bg-[#71DED6]/5"
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4" />
                    Share these results
                    <ArrowRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>

              {/* 2-column grid */}
              <div className="grid grid-cols-2 gap-4">
                <StatCard
                  icon={TrendingUp}
                  value={additionalJobsRounded}
                  label="Additional jobs / mo"
                  sublabel="From automated follow-up alone"
                />
                <StatCard
                  icon={DollarSign}
                  value={additionalMonthlyRevenue}
                  prefix="$"
                  label="Additional monthly revenue"
                  sublabel="Revenue you're currently leaving behind"
                />
                <StatCard
                  icon={Star}
                  value={additionalReviews}
                  label="Additional reviews / mo"
                  sublabel="New 5-star reviews on autopilot"
                />
                <div
                  className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-6 flex flex-col justify-center"
                  data-testid="breakeven-inline-card"
                >
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-[#5A5A7A] mb-2">Break-even</p>
                  <p className="text-3xl font-black text-[#F4F4F8] tabular-nums leading-none">
                    {breakEvenJobs}
                    <span className="text-base font-semibold text-[#8B8BA7] ml-1.5">job{breakEvenJobs !== 1 ? "s" : ""}</span>
                  </p>
                  <p className="text-xs text-[#4A4A6A] mt-2 leading-snug">
                    to fully pay for CleanBid/mo
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Breakdown Card ── */}
          <div
            className="mt-6 bg-[#111118]/60 border border-[#1E1E2E] rounded-2xl px-7 py-5"
            data-testid="breakeven-card"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-[#71DED6]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <DollarSign className="w-4 h-4 text-[#71DED6]" />
              </div>
              <p className="text-sm text-[#8B8BA7] leading-relaxed">
                At{" "}
                <span className="text-[#F4F4F8] font-semibold">$179/month</span>
                , CleanBid pays for itself after{" "}
                <span className="text-[#71DED6] font-bold">
                  {breakEvenJobs} additional job{breakEvenJobs !== 1 ? "s" : ""}
                </span>{" "}
                per month. At{" "}
                <span className="text-[#F4F4F8] font-semibold">${jobValue.toLocaleString()}/job</span>
                , that's typically recovered in the{" "}
                <span className="text-[#F4F4F8] font-semibold">first week</span>.
              </p>
            </div>
          </div>

          {/* ── CTA ── */}
          <div
            className="mt-10 text-center"
            data-testid="roi-cta-section"
          >
            <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[#F4F4F8] mb-2">
              Ready to stop leaving money on the table?
            </h3>
            <p className="text-[#8B8BA7] text-sm mb-7 max-w-sm mx-auto">
              See exactly how CleanBid works for your business in 20 minutes.
            </p>
            <a
              href={CALCOM_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="roi-cta-button"
              className="inline-flex items-center gap-2 bg-[#71DED6] text-[#10151D] font-semibold px-10 py-4 rounded-full text-base hover:scale-[1.02] hover:shadow-[0_0_24px_rgba(113,222,214,0.4)] transition-all duration-200"
            >
              Book a Demo
              <ArrowRight className="w-4 h-4" />
            </a>
            <p className="text-xs text-[#5A5A7A] mt-4">
              No credit card required. No contracts.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
